import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import type { RoomDetail, CreateRoomPayload, DiscountSlot } from '@/types/room';
import type { Branch } from '@/types/branch';
import * as branchService from '@/services/branchService';
import { Loader2, X, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
    AMENITY_OPTIONS,
    COMMON_AMENITIES,
    EXTRA_AMENITIES,
    findAmenityOption,
    getAmenityIcon,
    hasSharedWC,
    SHARED_WC_WARNING,
} from '@/data/amenities';
import { DiscountSlotsSection } from './DiscountSlotsSection';
import * as settingsService from '@/services/settingsService';
import type { FixedSlot } from '@/services/settingsService';

interface RoomFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (data: CreateRoomPayload) => Promise<void>;
    /** Khi truyền room, modal chuyển sang mode chỉnh sửa và pre-fill data */
    room?: RoomDetail | null;
}

interface FormErrors {
    name?: string;
    type?: string;
    hourlyRate?: string;
}

const ROOM_TYPE_OPTIONS = [
    { value: 'standard', label: 'Tiêu chuẩn' },
    { value: 'vip', label: 'VIP' },
    { value: 'supervip', label: 'Super VIP' },
] as const;

/**
 * Modal tạo/sửa phòng — hỗ trợ cả mode tạo mới và chỉnh sửa.
 * Hiển thị validation errors inline cho từng field.
 */
export function RoomFormModal({ isOpen, onClose, onSuccess, room }: RoomFormModalProps) {
    const isEditMode = !!room;

    // Form state
    const [name, setName] = useState('');
    const [type, setType] = useState<string>('');
    const [branchId, setBranchId] = useState<string>('');
    const [hourlyRate, setHourlyRate] = useState<string>('');
    const [dailyRate, setDailyRate] = useState<string>('0');
    const [overnightRate, setOvernightRate] = useState<string>('0');
    const [extraHourRate, setExtraHourRate] = useState<string>('0');
    const [combo3hRate, setCombo3hRate] = useState<string>('0');
    const [combo6h1hRate, setCombo6h1hRate] = useState<string>('0');
    const [combo6h1hDiscount, setCombo6h1hDiscount] = useState<string>('0');
    const [maxGuests, setMaxGuests] = useState<string>('2');
    const [description, setDescription] = useState('');
    const [amenities, setAmenities] = useState<string[]>([]);
    const [amenityInput, setAmenityInput] = useState('');
    const [discountSlots, setDiscountSlots] = useState<DiscountSlot[]>([]);
    const [globalSlots, setGlobalSlots] = useState<FixedSlot[]>([]);
    const [slotRates, setSlotRates] = useState<Record<string, { price: number; isActive: boolean }>>({});

    // UI state
    const [errors, setErrors] = useState<FormErrors>({});
    const [submitting, setSubmitting] = useState(false);
    const [branches, setBranches] = useState<Branch[]>([]);
    const [loadingBranches, setLoadingBranches] = useState(false);

    // Load branches on mount
    const loadBranches = useCallback(async () => {
        setLoadingBranches(true);
        try {
            const data = await branchService.getAll();
            setBranches(data);
        } catch {
            // Silently fail — branch dropdown will be empty
        } finally {
            setLoadingBranches(false);
        }
    }, []);

    const fetchGlobalSlots = useCallback(async () => {
        try {
            const slotsData = await settingsService.getFixedSlots();
            setGlobalSlots(slotsData.sort((a, b) => a.order - b.order));
        } catch {
            // Silently fail
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            loadBranches();
            fetchGlobalSlots();
        }
    }, [isOpen, loadBranches, fetchGlobalSlots]);

    // Pre-fill data khi edit mode hoặc reset khi tạo mới
    useEffect(() => {
        if (isOpen) {
            if (room) {
                setName(room.name);
                setType(room.type);
                setBranchId(room.branchId || '');
                setHourlyRate(String(room.hourlyRate));
                setDailyRate(String(room.dailyRate));
                setOvernightRate(String(room.overnightRate));
                setExtraHourRate(String(room.extraHourRate));
                setCombo3hRate(String(room.combo3hRate));
                setCombo6h1hRate(String(room.combo6h1hRate));
                setCombo6h1hDiscount(String(room.combo6h1hDiscount));
                setMaxGuests(String(room.maxGuests));
                setDescription(room.description || '');
                setAmenities(room.amenities || []);
                setDiscountSlots(room.discountSlots || []);
                setSlotRates(room.slotRates || {});
            } else {
                setName('');
                setType('');
                setBranchId('');
                setHourlyRate('');
                setDailyRate('0');
                setOvernightRate('0');
                setExtraHourRate('0');
                setCombo3hRate('0');
                setCombo6h1hRate('0');
                setCombo6h1hDiscount('0');
                setMaxGuests('2');
                setDescription('');
                setAmenities([]);
                setDiscountSlots([]);
                setSlotRates({});
            }
            setAmenityInput('');
            setErrors({});
            setSubmitting(false);
        }
    }, [isOpen, room]);

    /** Validate required fields, trả về true nếu hợp lệ */
    function validate(): boolean {
        const newErrors: FormErrors = {};

        if (!name.trim()) {
            newErrors.name = 'Tên phòng là bắt buộc';
        }

        if (!type) {
            newErrors.type = 'Loại phòng là bắt buộc';
        }

        const rateNum = Number(hourlyRate);
        if (!hourlyRate || isNaN(rateNum) || rateNum <= 0) {
            newErrors.hourlyRate = 'Giá theo giờ phải lớn hơn 0';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    /** Bật/tắt amenity từ menu predefined */
    function toggleAmenity(label: string) {
        setAmenities((prev) =>
            prev.includes(label) ? prev.filter((a) => a !== label) : [...prev, label],
        );
    }

    /** Thêm amenity custom (không có trong danh sách predefined) */
    function handleAmenityKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter') {
            e.preventDefault();
            const value = amenityInput.trim();
            if (value && !amenities.includes(value)) {
                setAmenities((prev) => [...prev, value]);
            }
            setAmenityInput('');
        }
    }

    /** Xoá amenity tag */
    function removeAmenity(tag: string) {
        setAmenities((prev) => prev.filter((a) => a !== tag));
    }

    const customAmenities = amenities.filter((a) => !findAmenityOption(a));

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();

        if (!validate()) return;

        const payload: CreateRoomPayload = {
            name: name.trim(),
            type: type as CreateRoomPayload['type'],
            branchId: (branchId && branchId !== 'none') ? branchId : null,
            description: description.trim() || null,
            images: room?.images || [],
            maxGuests: Number(maxGuests) || 2,
            amenities,
            hourlyRate: Number(hourlyRate) || 0,
            dailyRate: Number(dailyRate) || 0,
            overnightRate: Number(overnightRate) || 0,
            extraHourRate: Number(extraHourRate) || 0,
            combo3hRate: Number(combo3hRate) || 0,
            combo6h1hRate: Number(combo6h1hRate) || 0,
            combo6h1hDiscount: Number(combo6h1hDiscount) || 0,
            discountSlots,
            slotRates,
        };

        setSubmitting(true);
        try {
            await onSuccess(payload);
            onClose();
        } catch {
            // Error handling is done by the parent component (toast)
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>
                        {isEditMode ? 'Chỉnh sửa phòng' : 'Thêm phòng mới'}
                    </DialogTitle>
                    <DialogDescription>
                        {isEditMode
                            ? 'Cập nhật thông tin phòng bên dưới.'
                            : 'Nhập thông tin phòng mới bên dưới.'}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Tên phòng */}
                    <div className="space-y-1.5">
                        <Label htmlFor="room-name">
                            Tên phòng <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="room-name"
                            value={name}
                            onChange={(e) => {
                                setName(e.target.value);
                                if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                            }}
                            placeholder="Nhập tên phòng"
                            aria-invalid={!!errors.name}
                        />
                        {errors.name && (
                            <p className="text-sm text-destructive">{errors.name}</p>
                        )}
                    </div>

                    {/* Loại phòng */}
                    <div className="space-y-1.5">
                        <Label htmlFor="room-type">
                            Loại phòng <span className="text-destructive">*</span>
                        </Label>
                        <Select
                            value={type}
                            onValueChange={(val) => {
                                setType(val);
                                if (errors.type) setErrors((prev) => ({ ...prev, type: undefined }));
                            }}
                        >
                            <SelectTrigger className="w-full" aria-invalid={!!errors.type}>
                                <SelectValue placeholder="Chọn loại phòng" />
                            </SelectTrigger>
                            <SelectContent>
                                {ROOM_TYPE_OPTIONS.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {errors.type && (
                            <p className="text-sm text-destructive">{errors.type}</p>
                        )}
                    </div>

                    {/* Chi nhánh */}
                    <div className="space-y-1.5">
                        <Label htmlFor="room-branch">Chi nhánh</Label>
                        <Select
                            value={branchId}
                            onValueChange={setBranchId}
                        >
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder={loadingBranches ? 'Đang tải...' : 'Chọn chi nhánh (không bắt buộc)'} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">Không chọn</SelectItem>
                                {branches.map((b) => (
                                    <SelectItem key={b.id} value={b.id}>
                                        {b.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Giá theo giờ */}
                    <div className="space-y-1.5">
                        <Label htmlFor="room-hourly-rate">
                            Giá theo giờ (VNĐ) <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="room-hourly-rate"
                            type="number"
                            min="1"
                            value={hourlyRate}
                            onChange={(e) => {
                                setHourlyRate(e.target.value);
                                if (errors.hourlyRate) setErrors((prev) => ({ ...prev, hourlyRate: undefined }));
                            }}
                            placeholder="Nhập giá theo giờ"
                            aria-invalid={!!errors.hourlyRate}
                        />
                        {errors.hourlyRate && (
                            <p className="text-sm text-destructive">{errors.hourlyRate}</p>
                        )}
                    </div>

                    {/* Giá giờ phụ trội */}
                    <div className="space-y-1.5">
                        <Label htmlFor="room-extra-hour-rate">Giá giờ phụ trội (VNĐ)</Label>
                        <Input
                            id="room-extra-hour-rate"
                            type="number"
                            min="0"
                            value={extraHourRate}
                            onChange={(e) => setExtraHourRate(e.target.value)}
                            placeholder="0"
                        />
                    </div>

                    {/* Giá theo ngày */}
                    <div className="space-y-1.5">
                        <Label htmlFor="room-daily-rate">Giá theo ngày (VNĐ)</Label>
                        <Input
                            id="room-daily-rate"
                            type="number"
                            min="0"
                            value={dailyRate}
                            onChange={(e) => setDailyRate(e.target.value)}
                            placeholder="0"
                        />
                    </div>

                    {/* Giá qua đêm */}
                    <div className="space-y-1.5">
                        <Label htmlFor="room-overnight-rate">Giá qua đêm (VNĐ)</Label>
                        <Input
                            id="room-overnight-rate"
                            type="number"
                            min="0"
                            value={overnightRate}
                            onChange={(e) => setOvernightRate(e.target.value)}
                            placeholder="0"
                        />
                    </div>

                    {/* Giá Combo 3H */}
                    <div className="space-y-1.5">
                        <Label htmlFor="room-combo3h-rate">Giá Combo 3H (VNĐ)</Label>
                        <Input
                            id="room-combo3h-rate"
                            type="number"
                            min="0"
                            value={combo3hRate}
                            onChange={(e) => setCombo3hRate(e.target.value)}
                            placeholder="0"
                        />
                    </div>

                    {/* Giá Combo 6H+1H */}
                    <div className="space-y-1.5">
                        <Label htmlFor="room-combo6h1h-rate">Giá Combo 6H+1H (VNĐ)</Label>
                        <Input
                            id="room-combo6h1h-rate"
                            type="number"
                            min="0"
                            value={combo6h1hRate}
                            onChange={(e) => setCombo6h1hRate(e.target.value)}
                            placeholder="0"
                        />
                    </div>

                    {/* Giảm giá thay thế 1H */}
                    <div className="space-y-1.5">
                        <Label htmlFor="room-combo6h1h-discount">Giảm giá thay thế 1H (VNĐ)</Label>
                        <Input
                            id="room-combo6h1h-discount"
                            type="number"
                            min="0"
                            value={combo6h1hDiscount}
                            onChange={(e) => setCombo6h1hDiscount(e.target.value)}
                            placeholder="0"
                        />
                        <p className="text-xs text-muted-foreground">
                            Số tiền giảm khi khách không lấy 1H bonus
                        </p>
                    </div>

                    {/* Khung giờ giảm giá */}
                    <DiscountSlotsSection
                        value={discountSlots}
                        onChange={setDiscountSlots}
                        disabled={submitting}
                    />

                    {/* Cấu hình giá Khung giờ cố định */}
                    {globalSlots.length > 0 && (
                        <div className="space-y-3 rounded-lg border border-border p-4 bg-muted/20">
                            <div className="flex flex-col">
                                <Label className="font-bold text-sm">Giá Khung giờ cố định</Label>
                                <span className="text-[11px] text-muted-foreground mt-0.5">
                                    Tùy chỉnh giá và kích hoạt từng khung giờ cho phòng này. Nếu không điền giá, hệ thống tự động tính theo giá giờ tiêu chuẩn.
                                </span>
                            </div>
                            <div className="space-y-3.5 mt-2">
                                {globalSlots.map((slot) => {
                                    const current = slotRates[slot.id] || { price: 0, isActive: true };
                                    return (
                                        <div key={slot.id} className="flex items-center justify-between gap-3 text-sm">
                                            <div className="flex items-center gap-2.5">
                                                <input
                                                    type="checkbox"
                                                    id={`slot-active-${slot.id}`}
                                                    checked={current.isActive}
                                                    onChange={(e) => {
                                                        setSlotRates({
                                                            ...slotRates,
                                                            [slot.id]: { ...current, isActive: e.target.checked }
                                                        });
                                                    }}
                                                    className="h-4 w-4 rounded border-gray-300 text-zinc-900 focus:ring-zinc-900"
                                                />
                                                <Label htmlFor={`slot-active-${slot.id}`} className="font-medium hover:cursor-pointer text-xs">
                                                    {slot.name} ({slot.startTime}-{slot.endTime})
                                                </Label>
                                            </div>
                                            <div className="flex items-center gap-1.5 shrink-0 w-36">
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    value={current.price || ''}
                                                    disabled={!current.isActive}
                                                    onChange={(e) => {
                                                        setSlotRates({
                                                            ...slotRates,
                                                            [slot.id]: { ...current, price: Number(e.target.value) || 0 }
                                                        });
                                                    }}
                                                    placeholder="Tính tự động"
                                                    className="h-8 text-xs"
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Số khách tối đa */}
                    <div className="space-y-1.5">
                        <Label htmlFor="room-max-guests">Số khách tối đa</Label>
                        <Input
                            id="room-max-guests"
                            type="number"
                            min="1"
                            value={maxGuests}
                            onChange={(e) => setMaxGuests(e.target.value)}
                            placeholder="2"
                        />
                    </div>

                    {/* Mô tả */}
                    <div className="space-y-1.5">
                        <Label htmlFor="room-description">Mô tả</Label>
                        <Textarea
                            id="room-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Nhập mô tả phòng (không bắt buộc)"
                            rows={3}
                        />
                    </div>

                    {/* Tiện nghi — menu tags predefined + input custom */}
                    <div className="space-y-3">
                        <Label>Tiện nghi</Label>

                        <AmenityGroup
                            title="Tiện nghi chung"
                            options={COMMON_AMENITIES}
                            selected={amenities}
                            onToggle={toggleAmenity}
                        />

                        <AmenityGroup
                            title="Tiện nghi riêng"
                            options={EXTRA_AMENITIES}
                            selected={amenities}
                            onToggle={toggleAmenity}
                        />

                        {hasSharedWC(amenities) && (
                            <p className="text-xs font-medium text-red-600">
                                ⚠️ {SHARED_WC_WARNING}
                            </p>
                        )}

                        <div className="space-y-1.5">
                            <Label htmlFor="room-amenities" className="text-xs font-semibold text-muted-foreground">
                                Tiện nghi khác (tuỳ chỉnh)
                            </Label>
                            {customAmenities.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {customAmenities.map((tag) => (
                                        <span
                                            key={tag}
                                            className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-sm"
                                        >
                                            <span aria-hidden="true">{getAmenityIcon(tag)}</span>
                                            <span>{tag}</span>
                                            <button
                                                type="button"
                                                onClick={() => removeAmenity(tag)}
                                                className="ml-0.5 rounded-full p-0.5 hover:bg-destructive/10 hover:text-destructive"
                                                aria-label={`Xoá ${tag}`}
                                            >
                                                <X className="size-3" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                            <Input
                                id="room-amenities"
                                value={amenityInput}
                                onChange={(e) => setAmenityInput(e.target.value)}
                                onKeyDown={handleAmenityKeyDown}
                                placeholder="Nhập tiện nghi custom rồi nhấn Enter"
                            />
                        </div>
                    </div>

                    <DialogFooter className="pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            Huỷ
                        </Button>
                        <Button type="submit" disabled={submitting}>
                            {submitting && <Loader2 className="mr-2 size-4 animate-spin" />}
                            {isEditMode ? 'Cập nhật' : 'Tạo phòng'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

interface AmenityGroupProps {
    title: string;
    options: typeof AMENITY_OPTIONS;
    selected: string[];
    onToggle: (label: string) => void;
}

/** Grid checkbox pill cho một nhóm tiện nghi */
function AmenityGroup({ title, options, selected, onToggle }: AmenityGroupProps) {
    return (
        <div className="space-y-1.5">
            <p className="text-xs font-semibold text-muted-foreground">{title}</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {options.map((opt) => {
                    const active = selected.includes(opt.label);
                    const warn = opt.warn === true;
                    return (
                        <button
                            key={opt.id}
                            type="button"
                            onClick={() => onToggle(opt.label)}
                            className={cn(
                                'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs sm:text-sm transition-colors text-left',
                                active
                                    ? warn
                                        ? 'border-red-300 bg-red-100 text-red-700 font-semibold'
                                        : 'border-primary bg-primary/10 text-primary font-semibold'
                                    : warn
                                        ? 'border-red-200 bg-card text-red-600 hover:border-red-300 hover:bg-red-50'
                                        : 'border-border bg-card text-muted-foreground hover:border-primary/40 hover:bg-accent',
                            )}
                            aria-pressed={active}
                        >
                            <span aria-hidden="true">{opt.icon}</span>
                            <span className="flex-1 truncate">{opt.label}</span>
                            {active && <Check className="size-3.5 shrink-0" />}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
